import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ArrowRight, TrendingDown } from "lucide-react";
import heroProduct from "@/assets/hero-slide-1.webp";
import abstractBg from "@/assets/abstract-bg.webp";
import DemoScheduler from "./DemoScheduler";
import { track } from "@/lib/track";

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <>
      <section className="relative overflow-hidden bg-gradient-hero pt-28 pb-20 lg:pt-36 lg:pb-28">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: `url(${abstractBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <div className="container w-full min-w-0 mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-12 lg:gap-10 items-center">
            <div className="text-center lg:text-left">
              <p className="inline-block px-4 py-1.5 mb-6 rounded-full bg-accent text-sm font-medium text-foreground animate-fade-in-delay-1">
                {t('hero.eyebrow')}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight animate-fade-in-delay-1">
                {t('hero.title')} <span className="text-primary">{t('hero.highlight')}</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-delay-2">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start animate-fade-in-delay-4">
                <Button size="lg" className="group w-full sm:w-auto" onClick={() => { track("cta_click", { location: "hero" }); setDemoOpen(true); }}>
                  {t('hero.cta')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="ghost" className="h-auto min-h-11 whitespace-normal py-2" onClick={() => { track("guide_click", { location: "hero" }); navigate('/materiais'); }}>
                  {t('hero.cta1')}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-3 animate-fade-in-delay-4">
                {t('hero.ctaNote')}
              </p>

              <a
                href="/case-study/eleeva-it"
                onClick={(e) => { e.preventDefault(); track("case_click", { location: "hero" }); navigate('/case-study/eleeva-it'); }}
                className="mt-8 inline-flex items-center gap-3 rounded-xl border border-border bg-card/80 px-4 py-3 text-left text-sm hover:border-primary transition-colors animate-fade-in-delay-4"
              >
                <TrendingDown className="w-5 h-5 text-primary shrink-0" />
                <span>
                  <span className="font-semibold text-foreground">{t('hero.proof')}</span>
                  <span className="ml-2 text-muted-foreground underline underline-offset-2">{t('hero.proofLink')}</span>
                </span>
              </a>
            </div>

            <div className="relative animate-fade-in-delay-5 w-full">
              <div className="absolute inset-0 bg-primary/10 rounded-3xl blur-3xl" />
              <img
                src={heroProduct}
                alt={t('hero.imageAlt')}
                width={1280}
                height={720}
                className="relative w-full h-auto rounded-2xl lg:scale-110 lg:origin-left"
                loading="eager"
                decoding="sync"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      <DemoScheduler open={demoOpen} onOpenChange={setDemoOpen} source="hero" />
    </>
  );
};

export default Hero;
